// Entity Management page
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Stack, Paper, Grid, Divider, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, IconButton, Chip, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    DonutChart, StatusPill, CHART_COLORS,
} from '../agents/AgentUIKit';
import {
    listEntities, createEntity, updateEntity, deleteEntity,
    entityHierarchy, entitiesSummary,
} from './api';

const ENTITY_TYPES = ['PARENT', 'SUBSIDIARY', 'BRANCH', 'DIVISION', 'JOINT_VENTURE', 'ELIMINATION'];
const CONSOL_METHODS = ['FULL', 'PROPORTIONAL', 'EQUITY', 'NONE'];
const STATUSES = ['ACTIVE', 'INACTIVE', 'DIVESTED'];

const DEFAULT_FORM = {
    entity_code: '',
    entity_name: '',
    legal_name: '',
    entity_type: 'SUBSIDIARY',
    functional_currency: 'USD',
    reporting_currency: 'USD',
    fiscal_year_start_month: 1,
    consolidation_method: 'FULL',
    ownership_pct: 100,
    parent_entity_id: '',
    country: '',
    tax_id: '',
    address: '',
    is_elimination_entity: false,
};

export default function Entities() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [entities, setEntities] = useState([]);
    const [summary, setSummary] = useState(null);
    const [hierarchy, setHierarchy] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'tree'

    const [dialog, setDialog] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(DEFAULT_FORM);

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadAll = async () => {
        const [l, s, h] = await Promise.allSettled([
            listEntities(), entitiesSummary(), entityHierarchy(),
        ]);
        if (l.status === 'fulfilled' && l.value?.data?.success) setEntities(l.value.data.data);
        if (s.status === 'fulfilled' && s.value?.data?.success) setSummary(s.value.data.data);
        if (h.status === 'fulfilled' && h.value?.data?.success) setHierarchy(h.value.data.data);
    };

    useEffect(() => { run(loadAll); }, []);

    const openCreate = () => {
        setEditId(null);
        setForm(DEFAULT_FORM);
        setDialog(true);
    };

    const openEdit = (e) => {
        setEditId(e.id);
        setForm({
            entity_code: e.entity_code,
            entity_name: e.entity_name,
            legal_name: e.legal_name || '',
            entity_type: e.entity_type || 'SUBSIDIARY',
            functional_currency: e.functional_currency || 'USD',
            reporting_currency: e.reporting_currency || 'USD',
            fiscal_year_start_month: e.fiscal_year_start_month || 1,
            consolidation_method: e.consolidation_method || 'FULL',
            ownership_pct: e.ownership_pct || 100,
            parent_entity_id: e.parent_entity_id || '',
            country: e.country || '',
            tax_id: e.tax_id || '',
            address: e.address || '',
            is_elimination_entity: !!e.is_elimination_entity,
            status: e.status,
        });
        setDialog(true);
    };

    const submit = async () => {
        const body = { ...form };
        if (!body.parent_entity_id) delete body.parent_entity_id;
        const r = await run(() => editId ? updateEntity(editId, body) : createEntity(body));
        if (r?.data?.success) {
            setDialog(false);
            await loadAll();
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Mark this entity as inactive?')) return;
        const r = await run(() => deleteEntity(id));
        if (r?.data?.success) await loadAll();
    };

    // Summary cards
    const summaryCards = [
        { label: 'Total Entities', value: summary?.total_entities ?? '—', accent: 'primary' },
        { label: 'Active', value: summary?.active_entities ?? '—', accent: 'success' },
        { label: 'Inactive', value: summary?.inactive_entities ?? '—', accent: 'neutral' },
        { label: 'Currencies', value: summary?.currency_count ?? '—', accent: 'info' },
    ];

    const entitiesByTypeData = summary?.entities_by_type
        ? Object.entries(summary.entities_by_type).map(([name, value]) => ({ name, value }))
        : [];

    // Build parent options (exclude self on edit)
    const parentOptions = entities.filter(e => !editId || e.id !== editId);

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Entities</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Multi-Entity Management — configure legal entities, subsidiaries, branches, and divisions
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={() => run(loadAll)}><RefreshIcon /></IconButton>
                        <Button variant="outlined" size="small" startIcon={<AccountTreeIcon />}
                            onClick={() => setViewMode(viewMode === 'list' ? 'tree' : 'list')}>
                            {viewMode === 'list' ? 'Tree View' : 'List View'}
                        </Button>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
                            New Entity
                        </Button>
                    </Stack>
                </Stack>
                {error && <Typography variant="caption" color="error">{error}</Typography>}

                <SummaryGrid cards={summaryCards} />

                {entitiesByTypeData.length > 0 && (
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Entities by Type</Typography>
                                <DonutChart data={entitiesByTypeData} height={220} />
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="caption" fontWeight={600}>Currencies in Use</Typography>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                    {(summary?.currencies_used || []).map(c => (
                                        <Chip key={c} label={c} size="small" variant="outlined" />
                                    ))}
                                    {!summary?.currencies_used?.length && <Typography variant="caption" color="text.secondary">No currencies yet</Typography>}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                <Divider />

                {loading && !entities.length ? <LoadingBlock rows={4} /> : viewMode === 'tree' ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <SectionHeader title="Entity Hierarchy" />
                        {(hierarchy || []).length === 0 ? <EmptyState message="No entities yet" /> : (
                            <Stack spacing={0.5}>
                                {hierarchy.map(root => <TreeNode key={root.id} node={root} level={0} />)}
                            </Stack>
                        )}
                    </Paper>
                ) : (
                    <DrillTable
                        columns={[
                            { key: 'entity_code', label: 'Code' },
                            { key: 'entity_name', label: 'Name' },
                            { key: 'entity_type', label: 'Type', render: r => <Chip size="small" label={r.entity_type} variant="outlined" /> },
                            { key: 'functional_currency', label: 'Currency' },
                            { key: 'consolidation_method', label: 'Consol', render: r => <Chip size="small" label={r.consolidation_method} variant="outlined" /> },
                            { key: 'ownership_pct', label: 'Own %', align: 'right', render: r => `${r.ownership_pct}%` },
                            { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                            { key: 'actions', label: '', render: r => (
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Mark inactive"><IconButton size="small" color="error" onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                </Stack>
                            )},
                        ]}
                        rows={entities}
                        maxHeight={520}
                    />
                )}
            </Stack>

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editId ? 'Edit Entity' : 'New Entity'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={12} md={4}>
                            <TextField size="small" fullWidth required label="Entity Code" value={form.entity_code}
                                onChange={e => setForm({ ...form, entity_code: e.target.value })} disabled={!!editId} />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TextField size="small" fullWidth required label="Entity Name" value={form.entity_name}
                                onChange={e => setForm({ ...form, entity_name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Legal Name" value={form.legal_name}
                                onChange={e => setForm({ ...form, legal_name: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth select label="Type" value={form.entity_type}
                                onChange={e => setForm({ ...form, entity_type: e.target.value })}>
                                {ENTITY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth select label="Consol Method" value={form.consolidation_method}
                                onChange={e => setForm({ ...form, consolidation_method: e.target.value })}>
                                {CONSOL_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth label="Func Currency" value={form.functional_currency}
                                onChange={e => setForm({ ...form, functional_currency: e.target.value.toUpperCase().slice(0, 3) })} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth label="Reporting" value={form.reporting_currency}
                                onChange={e => setForm({ ...form, reporting_currency: e.target.value.toUpperCase().slice(0, 3) })} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth type="number" label="Own %" value={form.ownership_pct}
                                onChange={e => setForm({ ...form, ownership_pct: parseFloat(e.target.value) || 0 })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth select label="Parent Entity (optional)" value={form.parent_entity_id || ''}
                                onChange={e => setForm({ ...form, parent_entity_id: e.target.value })}>
                                <MenuItem value=""><em>None (top-level)</em></MenuItem>
                                {parentOptions.map(p => <MenuItem key={p.id} value={p.id}>{p.entity_code} — {p.entity_name}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth type="number" label="FY Start Month" value={form.fiscal_year_start_month}
                                onChange={e => setForm({ ...form, fiscal_year_start_month: parseInt(e.target.value) || 1 })}
                                inputProps={{ min: 1, max: 12 }} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth label="Country (2-char)" value={form.country}
                                onChange={e => setForm({ ...form, country: e.target.value.toUpperCase().slice(0, 2) })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField size="small" fullWidth label="Tax ID" value={form.tax_id}
                                onChange={e => setForm({ ...form, tax_id: e.target.value })} />
                        </Grid>
                        {editId && (
                            <Grid item xs={12} md={6}>
                                <TextField size="small" fullWidth select label="Status" value={form.status || 'ACTIVE'}
                                    onChange={e => setForm({ ...form, status: e.target.value })}>
                                    {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                </TextField>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth multiline rows={2} label="Address" value={form.address}
                                onChange={e => setForm({ ...form, address: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submit} disabled={!form.entity_code || !form.entity_name || loading}>
                        {editId ? 'Save' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function TreeNode({ node, level }) {
    return (
        <Box>
            <Paper variant="outlined" sx={{ p: 1, ml: level * 3, borderLeft: `3px solid ${CHART_COLORS.primary}` }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={node.entity_type} variant="outlined" />
                    <Typography variant="body2" fontWeight={600}>{node.entity_code}</Typography>
                    <Typography variant="body2" color="text.secondary">— {node.entity_name}</Typography>
                    <Chip size="small" variant="outlined" label={node.functional_currency} />
                    <Typography variant="caption" color="text.secondary">({node.ownership_pct}%)</Typography>
                </Stack>
            </Paper>
            {(node.children || []).map(c => <TreeNode key={c.id} node={c} level={level + 1} />)}
        </Box>
    );
}
